/**
 * The ops app's PrimeVue preset — ClaimShield purple on top of Aura.
 *
 * Lives in its own module so every entry that mounts real ops components
 * (`main.ts`, and the dev review entries under `src/dev/`) uses the SAME preset
 * object rather than a duplicate or a silent fallback to stock Aura. Before this
 * module existed the preset was an un-exported const inside `main.ts`, so
 * `src/dev/client-support-review.ts` could only import plain `Aura` and rendered
 * every primary-derived surface in Aura's emerald (measured: `--p-primary-color`
 * = #10b981) while the real app rendered #3d2683.
 */
import Aura from '@primevue/themes/aura'
import { definePreset } from '@primevue/themes'

// Aura ships an emerald primary ramp. Re-point it at the ClaimShield purple
// scale (knowledge/brand/tokens.md) so unstyled PrimeVue controls render on
// brand and green is left to mean `success` only.
//
// The ramp alone is not enough. Aura resolves `primary.color` to `{primary.500}`,
// but the brand anchor is #3d2683, which is 700 on our scale — so a ramp-only
// preset ships purple-500 (#7952b3) as the action colour and leaves the anchor
// unused. Measured in the running app before this block existed: primary.color
// and focus-ring.color both #7952b3. Buttons hid it (primevue-overrides.css
// paints them with the canon gradient), but focus rings, checkboxes, toggles,
// radio fills, tab underlines and links all took the 500. Pin the colorScheme
// so every primary-derived surface lands on the anchor.
//
// `highlight` is deliberately NOT overridden — Aura's defaults already resolve
// to {primary.50}/{primary.700} = #f5f3fa/#3d2683, which is exactly the canon's
// table-header spec. Same for focusRing's geometry: it inherits primary.color,
// so it turns purple here without weakening the 1px/2px-offset ring that is the
// keyboard-focus affordance.
//
// packages/portal and packages/driver-portal carry the ramp-only version of this
// block and still resolve primary.color to #7952b3.
export const ClaimShieldAura = definePreset(Aura, {
  semantic: {
    primary: {
      50: '#f5f3fa',
      100: '#ebe6f5',
      200: '#d4c9eb',
      300: '#b8a3dc',
      400: '#9575c9',
      500: '#7952b3',
      600: '#6b4faf',
      700: '#3d2683',
      800: '#2a1a5e',
      900: '#1c1240',
      950: '#0e0920',
    },
    colorScheme: {
      light: {
        primary: {
          color: '{primary.700}',
          contrastColor: '#ffffff',
          hoverColor: '{primary.800}',
          activeColor: '{primary.900}',
        },
      },
    },
  },
})
